import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem, Heading, Show, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {GetManufacturersResponse} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {BillingInformationListItemResponseBody, BranchListResponseBody} from '@dms/api/shared';
import {GetSpacesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getBillingInformationOptions,
  getOptionsFromAuthorizationProfiles,
  getOptionsFromBranches,
  getOptionsFromManufacturers,
} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDetailForm} from '../types/WarehouseDetailForm';
import {getSpaceOptions} from '../utils/getSpaceOptions';

interface GeneralFormProps extends TestIdProps {
  control: FormControl<WarehouseDetailForm>;
  branches: BranchListResponseBody | Nullish;
  billingInformations: BillingInformationListItemResponseBody[] | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  manufacturers: GetManufacturersResponse | Nullish;
  spaces: GetSpacesResponse | Nullish;
  isEditing: boolean;
  onAuthorizationProfileChange: (authorizationProfileId: string | number | null) => void;
  isAuthorizationProfileFetching: boolean;
}

export function GeneralForm(props: GeneralFormProps) {
  const selectedBillingInformationId = useWatch({
    control: props.control,
    name: 'companyBillingInfoId',
  });
  const selectedAuthorizationProfileId = useWatch({
    control: props.control,
    name: 'authorizationProfileId',
  });

  return (
    <VStack spacing={6}>
      <Heading size={3}>{i18n.t('general.labels.general')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="name"
            type="text"
            label={i18n.t('general.labels.name')}
            isRequired
            data-testid={suffixTestId('inputs.name', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            isRequired
            control={props.control}
            name="warehouseNumber"
            type="text"
            label={i18n.t('entity.warehouse.labels.code')}
            data-testid={suffixTestId('inputs.warehouseNumber', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="authorizationProfileId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.authorizationProfile')}
            options={getOptionsFromAuthorizationProfiles(
              props.authorizationProfiles,
              selectedAuthorizationProfileId
            )}
            onChange={props.onAuthorizationProfileChange}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.authorizationProfile', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="companyBillingInfoId"
            type="choice"
            label={i18n.t('general.labels.company')}
            options={getBillingInformationOptions({
              billingInformations: props.billingInformations,
              selectedId: selectedBillingInformationId,
              labelKey: 'companyName',
            })}
            isRequired
            isNotClearable
            data-testid={suffixTestId('inputs.companyBillingInfoId', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="branchId"
            type="choice"
            label={i18n.t('entity.branch.labels.branch')}
            options={getOptionsFromBranches(props.branches)}
            isLoading={props.isAuthorizationProfileFetching}
            isRequired
            isNotClearable
            isDisabled
            data-testid={suffixTestId('inputs.branchId', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="supportedMake"
            type="choice"
            label={i18n.t('entity.warehouse.labels.make')}
            data-testid={suffixTestId('inputs.supportedMake', props)}
            options={[]}
          />
        </GridItem>
      </Grid>

      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            control={props.control}
            name="defaultManufacturerId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.defaultManufacturer')}
            options={getOptionsFromManufacturers(props.manufacturers)}
            data-testid={suffixTestId('inputs.defaultManufacturer', props)}
          />
        </GridItem>
        {/* spaces can only be assigned when editing, the create (POST) endpoint does not support them */}
        <Show when={props.isEditing}>
          <GridItem span={1}>
            <FormField
              control={props.control}
              name="spaceIds"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.spaces')}
              options={getSpaceOptions(props.spaces)}
              data-testid={suffixTestId('inputs.spaces', props)}
            />
          </GridItem>
        </Show>
      </Grid>
    </VStack>
  );
}
